import { buttonStyle } from 'components/buttons/buttonStyle';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import PercentageInput from 'components/forms/inputs/rhf-inputs/PercentageInput';
import TextInput from 'components/forms/inputs/rhf-inputs/TextInput';
import { ModalFooterSimple } from 'components/modals/ModalFooterSimple';
import { ModalHeader } from 'components/modals/ModalHeader';
import { Button } from 'components/ui/button';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useSharedPayment } from 'pages/invoices/payers/useSharedPayment';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { GroupingInvoiceRowEditForm, InvoiceData } from 'types/invoices';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { paymentCurrencyInputClassName, usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import PaymentCurrencyNotice from '../components/PaymentCurrencyNotice';

interface Props {
    titleId: string;
    initialData: GroupingInvoiceRowEditForm;
    onClose: () => void;
    handleUpdateTotals: (data: GroupingInvoiceRowEditForm) => void;
    invoice: InvoiceData;
}

export const GroupingInvoiceRowEditModal = (props: Props) => {
    const { titleId, onClose, handleUpdateTotals, initialData, invoice } = props;
    const { isSharedPaymentCopy } = useSharedPayment(invoice);
    // A shared-payment copy's amounts are the group's; only a regular copy edits amounts and deletes rows here.
    const isSplitInvoice = (invoice.isSplit ?? false) && !isSharedPaymentCopy;
    const isInViewMode = !invoice.isEditable;
    const { isPaymentCurrencyMode, displayCurrency } = usePaymentCurrencyMode(invoice);

    const { handleSubmit, control, setValue, getValues } = useForm<GroupingInvoiceRowEditForm>({
        defaultValues: initialData,
        values: initialData,
    });

    const handleOnSubmit = (data: GroupingInvoiceRowEditForm) => {
        handleUpdateTotals(data);
        onClose();
    };

    const onDelete = () => {
        setValue('isDeleted', true);
        handleSubmit((data) => handleOnSubmit(data))();
    };

    const onAmountBeforeDiscount = useCallback(
        (value: string | undefined) => {
            const discountRate: number | undefined = getValues('discountRate');
            if (value && !isNaN(Number(value))) {
                const amountBeforeDiscount = Number(value);
                const discountAmount = !discountRate
                    ? 0
                    : Math.round(((amountBeforeDiscount * discountRate) / 100) * 100) / 100;
                const itemTotal = amountBeforeDiscount - discountAmount;
                setValue('totalAmount', Math.round(itemTotal * 100) / 100);
                setValue('discountAmount', discountAmount === 0 ? '0' : (discountAmount * -1).toFixed(2));
            } else {
                setValue('totalAmount', 0);
                setValue('discountAmount', '0');
            }
        },
        [setValue, getValues]
    );

    const onDiscountRateChange = useCallback(
        (value: string | undefined) => {
            const amountBeforeDiscount: number | undefined = getValues('amountBeforeDiscount');
            if (value && !isNaN(Number(value))) {
                const discountRate = Number(value);
                const discountAmount = Math.round((((amountBeforeDiscount ?? 0) * discountRate) / 100) * 100) / 100;
                const itemTotal = (amountBeforeDiscount ?? 0) - discountAmount;
                setValue('totalAmount', Math.round(itemTotal * 100) / 100);
                setValue('discountAmount', discountAmount === 0 ? '0' : (discountAmount * -1).toFixed(2));
            } else {
                setValue('totalAmount', amountBeforeDiscount || 0);
                setValue('discountAmount', '0');
            }
        },
        [setValue, getValues]
    );

    const onDiscountAmountChange = useCallback(
        (value: string | undefined) => {
            const amountBeforeDiscount: number | undefined = getValues('amountBeforeDiscount');
            if (value && !isNaN(Number(value)) && amountBeforeDiscount) {
                const discountAmount = Math.abs(Number(value));
                const itemTotal = (amountBeforeDiscount ?? 0) - discountAmount;
                setValue('totalAmount', Math.round(itemTotal * 100) / 100);
                const disaountRate = (discountAmount / amountBeforeDiscount) * 100;
                const roundingValue = Math.pow(10, 7);
                setValue('discountRate', Math.round(disaountRate * roundingValue) / roundingValue);
            } else {
                setValue('totalAmount', 0);
                setValue('discountRate', 0);
            }
        },
        [setValue, getValues]
    );

    const actions = [
        <Button key="cancel" {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={onClose}>
            {t('cancel')}
        </Button>,
    ];

    if (!isInViewMode) {
        actions.push(
            <Button
                key="update"
                {...buttonStyle({ variant: 'contained', color: 'secondary' })}
                onClick={(e) => {
                    void handleSubmit(handleOnSubmit)(e);
                }}
            >
                {t('invoices.totals-tab.update')}
            </Button>
        );
    }

    if (!isInViewMode && isSplitInvoice) {
        actions.push(
            <Button key="delete" {...buttonStyle({ variant: 'outlined', color: 'primary' })} onClick={onDelete}>
                {t('delete')}
            </Button>
        );
    }

    return (
        <>
            <ModalHeader
                kit
                title={t(titleId)}
                description={
                    <FormattedMessage
                        id={
                            initialData.sourceType === 'EXPENSE'
                                ? 'invoices.totals-tab.update-expense-description'
                                : 'invoices.totals-tab.update-fee-description'
                        }
                        values={{
                            case:
                                invoice.invoiceMatters?.find((matter) => matter.matterId === initialData.matterId)
                                    ?.matterNumber ?? '',
                            amount: <MoneyDisplay amount={initialData.totalAmount ?? 0} currency={displayCurrency} />,
                        }}
                    />
                }
                onCancel={onClose}
            />
            <div className="h-full overflow-y-auto p-6 max-sm:px-4">
                {isPaymentCurrencyMode && isSplitInvoice && (
                    <PaymentCurrencyNotice currency={displayCurrency} className="mb-4" />
                )}
                {/* A 12-column grid with 16px gutters; the amounts start a row of their own under the description. */}
                <div className="grid grid-cols-12 gap-4">
                    <div className="new:col-span-12 col-span-8">
                        <TextInput control={control} name="description" label="invoices.totals-tab.description" />
                    </div>
                    {isSplitInvoice ? (
                        <>
                            <div
                                className={cn(
                                    'col-span-3 col-start-1',
                                    isPaymentCurrencyMode && paymentCurrencyInputClassName
                                )}
                            >
                                <DecimalInput
                                    control={control}
                                    name="amountBeforeDiscount"
                                    label="invoices.totals-tab.amount-wo-vat"
                                    onChange={(event) => onAmountBeforeDiscount(event.target.value)}
                                />
                            </div>
                            <div className={cn('col-span-3', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                <PercentageInput
                                    control={control}
                                    name="discountRate"
                                    decimalPlaces={7}
                                    label="invoices.totals-tab.discount"
                                    onChange={(event) => onDiscountRateChange(event.target.value)}
                                    validate={(value) => {
                                        if (typeof value === 'number' && value > 100) {
                                            return t('validation.cannot-exceed-pct', { max: '100' });
                                        }
                                        return true;
                                    }}
                                />
                            </div>
                            <div className={cn('col-span-3', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                <DecimalInput
                                    control={control}
                                    onlyNegative
                                    name="discountAmount"
                                    label={t('invoices.totals-tab.discount-currency', { currency: displayCurrency })}
                                    shouldTranslateLabel={false}
                                    onChange={(event) => onDiscountAmountChange(event.target.value)}
                                />
                            </div>
                            <div className={cn('col-span-3', isPaymentCurrencyMode && paymentCurrencyInputClassName)}>
                                <TextInput
                                    control={control}
                                    label="invoices.totals-tab.total-wo-vat"
                                    name="totalAmount"
                                    disabled
                                    required
                                />
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
            <ModalFooterSimple kit buttons={actions} />
        </>
    );
};
